import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, ImageIcon, X, Loader2, Heart, Video, ShieldCheck, Eye, EyeOff, Check, AlertCircle, Camera, Info } from 'lucide-react';
import supabase from '../supabaseClient';
import MemoryViewer from '../components/MemoryViewer';

const MEMORY_TYPES = [
  {
    id: 'voice',
    label: 'Voice message',
    description: 'Record a heartfelt message in your own voice',
    Icon: Mic,
  },
  {
    id: 'video',
    label: 'Video message',
    description: 'Share a short video — up to 30 seconds',
    Icon: Video,
  },
  {
    id: 'photo',
    label: 'Photo upload',
    description: 'Add up to 3 photos to preserve this moment',
    Icon: ImageIcon,
  },
];

const MemoryUploadPage = () => {
  const params = useParams();
  const qr_code = params.qr_code || params.qrCode || '';

  const [status, setStatus] = useState('loading');
  const [memory, setMemory] = useState(null);
  const [error, setError] = useState('');

  const [recipientName, setRecipientName] = useState('');
  const [memoryType, setMemoryType] = useState(''); // '', 'voice', 'video', 'photo'
  const [audioFile, setAudioFile] = useState(null);
  // Photos are stored as structured items so we can show previews + per-photo
  // upload state. Shape: { id, file, previewUrl, status, url?, errorMessage? }
  // status ∈ 'ready' | 'uploading' | 'done' | 'error'
  const [imageItems, setImageItems] = useState([]);
  const [imageError, setImageError] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [videoError, setVideoError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  // Refs for file inputs — used as an Android-safe fallback to trigger the
  // native picker imperatively when the label->input click path is flaky
  // (happens on some Android/Samsung/in-app browsers).
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Optional password protection
  const [protectEnabled, setProtectEnabled] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdHint, setPwdHint] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  // True when the page is being viewed inside a known in-app WebView
  // (Instagram / Facebook / LinkedIn / TikTok / Snapchat / etc). These
  // WebViews have notoriously unreliable file pickers — we show a gentle
  // hint so the user can open the page in Chrome/Safari instead.
  const [isInApp, setIsInApp] = useState(false);
  // True once the user has actually attempted a photo pick, so we can
  // differentiate "just cancelled" vs "picker returned nothing" and
  // surface an actionable error on the 2nd silent return.
  const pickAttemptedRef = useRef(false);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const ua = navigator.userAgent || '';
    const inApp = /FBAN|FBAV|FB_IAB|Instagram|LinkedInApp|Line\/|Twitter|Pinterest|MicroMessenger|TikTok|Snapchat|KAKAOTALK/i.test(ua);
    setIsInApp(inApp);
  }, []);

  const fetchMemory = useCallback(async () => {
    try {
      const { data, error: fetchErr } = await supabase
        .from('memory_entries')
        .select('*')
        .eq('qr_code', qr_code)
        .order('created_at', { ascending: false })
        .limit(1);

      if (fetchErr) {
        setError(fetchErr.message || 'Something went wrong.');
        setStatus('error');
        return;
      }

      const row = data?.[0];

      if (!row) {
        setError("We couldn't find a memory linked to this code.");
        setStatus('error');
        return;
      }

      setMemory(row);
      if (row.is_locked || row.Status === 'uploaded') {
        setStatus('locked');
      } else {
        setStatus('ready');
        if (row.recipient_name) setRecipientName(row.recipient_name);
      }
    } catch (e) {
      setError(e.message || 'Connection failed. Please check your internet and try again.');
      setStatus('error');
    }
  }, [qr_code]);

  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  // Release object URLs when photos are removed or page unmounts to avoid leaks
  useEffect(() => {
    return () => {
      imageItems.forEach((it) => {
        if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeSelect = (type) => {
    if (type === memoryType) return;
    // Clear any previously selected files when switching type to enforce single-type submission
    setAudioFile(null);
    setVideoFile(null);
    imageItems.forEach((it) => it.previewUrl && URL.revokeObjectURL(it.previewUrl));
    setImageItems([]);
    setImageError('');
    setVideoError('');
    setMemoryType(type);
  };

  const handleVideoAdd = (e) => {
    const file = e.target.files && e.target.files[0];
    // Always reset so the same file can be re-picked on Android if needed
    e.target.value = '';
    if (!file) return;
    setVideoError('');
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src);
      if (video.duration > 30) {
        setVideoError('Video must be 30 seconds or less.');
        setVideoFile(null);
      } else {
        setVideoFile(file);
      }
    };
    video.onerror = () => {
      setVideoFile(file);
    };
    video.src = URL.createObjectURL(file);
  };

  const handleImageAdd = (e) => {
    // Read files immediately — some Android browsers clear e.target.files
    // after a microtask, so we must capture synchronously.
    const picked = e.target.files ? Array.from(e.target.files) : [];
    // Reset the input so re-picking the same file works on Android.
    e.target.value = '';
    setImageError('');

    if (picked.length === 0) {
      // onChange can fire with 0 files when the user cancels the picker OR
      // when an Android content-provider silently fails (e.g. in-app WebView,
      // permission denied, or a broken gallery app). The first silent return
      // is usually a cancel — stay quiet. A second silent return right after
      // a real attempt almost always means the picker is broken on this
      // device, so show an actionable message.
      if (pickAttemptedRef.current) {
        setImageError(
          isInApp
            ? 'Your photo didn\u2019t come through. Please open this page in Chrome or Safari and try again.'
            : 'Your photo didn\u2019t come through. Please try again, or use the other photo button.'
        );
      }
      pickAttemptedRef.current = true;
      return;
    }
    // Reset the attempt flag once we successfully got a file.
    pickAttemptedRef.current = false;

    // Only accept images — Android camera apps sometimes return odd MIME types,
    // so be permissive: trust `accept="image/*"`, but also filter out zero-byte
    // files which indicate a failed camera intent.
    const valid = picked.filter((f) => f && f.size > 0 && (f.type ? f.type.startsWith('image/') : true));
    if (valid.length === 0) {
      setImageError('That photo couldn\u2019t be read. Please try choosing it again.');
      return;
    }

    setImageItems((prev) => {
      const allowed = 3 - prev.length;
      if (allowed <= 0) {
        setImageError('You can add up to 3 photos.');
        return prev;
      }
      const toAdd = valid.slice(0, allowed).map((file) => {
        // createObjectURL can throw on exotic Android file providers — never
        // let that silently swallow the selection. The list item will still
        // render (with filename + a generic icon) so the user knows it worked.
        let previewUrl = null;
        try {
          previewUrl = URL.createObjectURL(file);
        } catch (e) {
          previewUrl = null;
        }
        return {
          id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
          file,
          previewUrl,
          status: 'ready',
          url: null,
          errorMessage: null,
        };
      });
      if (valid.length > allowed) {
        setImageError(`Only ${allowed} more ${allowed === 1 ? 'photo' : 'photos'} can be added.`);
      }
      return [...prev, ...toAdd];
    });
  };

  const removeImage = (id) => {
    setImageItems((prev) => {
      const next = prev.filter((it) => {
        if (it.id === id && it.previewUrl) URL.revokeObjectURL(it.previewUrl);
        return it.id !== id;
      });
      return next;
    });
    setImageError('');
  };

  // Imperative trigger — the most reliable way to open the file picker
  // across Android versions (including in-app WebViews where label->input
  // click can silently fail).
  const openPicker = (ref) => {
    if (ref && ref.current) {
      try { ref.current.click(); } catch { /* noop */ }
    }
  };

  // Task 13 — direct-to-Supabase upload removed. The browser no longer
  // writes to Supabase Storage. All customer media (audio / image / video)
  // is posted as multipart form data to /api/memory/{qr}/upload, which uses
  // the backend service-role key against the (private) Supabase buckets.
  const uploadFile = async () => {
    throw new Error('Direct Supabase Storage upload disabled — route through /api/memory/{qr}/upload');
  };

  const hasValidMemoryFile = () => {
    if (memoryType === 'voice') return !!audioFile;
    if (memoryType === 'video') return !!videoFile && !videoError;
    if (memoryType === 'photo') return imageItems.length > 0;
    return false;
  };

  const canSubmit = memoryType && hasValidMemoryFile() && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!memoryType) {
      setError('Please choose how you would like to leave your memory.');
      return;
    }
    if (!hasValidMemoryFile()) return;

    // Enforce single-type submission — hard guard in case state ever has extras
    const typesPresent = [
      !!audioFile,
      !!videoFile,
      imageItems.length > 0,
    ].filter(Boolean).length;
    if (typesPresent !== 1) {
      setError('Only one memory type can be submitted. Please choose a single option.');
      return;
    }

    // Validate optional password
    setPwdError('');
    if (protectEnabled) {
      if (!pwd || pwd.length < 4) {
        setPwdError('Please choose a password with at least 4 characters.');
        return;
      }
      if (pwd !== pwdConfirm) {
        setPwdError('The passwords don’t match. Please try again.');
        return;
      }
    }

    setSubmitting(true);
    try {
      // Task 13 — route the upload through the backend so the browser no
      // longer needs Supabase Storage write permission. The server writes
      // to the private buckets with its service-role key, atomically sets
      // is_locked/Status/updated_at on the row, and returns signed URLs.
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const form = new FormData();
      if (recipientName.trim()) form.append('recipient_name', recipientName.trim());

      if (memoryType === 'voice' && audioFile) {
        form.append('audio', audioFile, audioFile.name);
      } else if (memoryType === 'video' && videoFile) {
        form.append('video', videoFile, videoFile.name);
      } else if (memoryType === 'photo' && imageItems.length > 0) {
        // Mark each image as uploading so the UI reflects progress on mobile
        setImageItems((prev) => prev.map((it) => ({ ...it, status: 'uploading', errorMessage: null })));
        imageItems.forEach((it) => form.append('images', it.file, it.file.name));
      }

      const uploadRes = await fetch(`${backendUrl}/api/memory/${encodeURIComponent(qr_code)}/upload`, {
        method: 'POST',
        body: form,
      });

      if (!uploadRes.ok) {
        const detail = await uploadRes.json().catch(() => ({}));
        if (memoryType === 'photo') {
          setImageItems((prev) => prev.map((it) => ({ ...it, status: 'error', errorMessage: detail.detail || 'Upload failed' })));
        }
        throw new Error(detail.detail || `Upload failed (${uploadRes.status})`);
      }

      if (memoryType === 'photo') {
        // Mark all photos done on success (the backend accepts the batch atomically).
        setImageItems((prev) => prev.map((it) => ({ ...it, status: 'done' })));
      }

      // If the sender chose to protect this memory, store the password
      // server-side (hashed) after the upload succeeds.
      if (protectEnabled && pwd) {
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL;
          if (backendUrl) {
            const res = await fetch(`${backendUrl}/api/memory/${encodeURIComponent(qr_code)}/set-password`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ password: pwd, hint: pwdHint.trim() || null }),
            });
            if (!res.ok) {
              // Don't block the save — but surface a soft note
              const detail = await res.json().catch(() => ({}));
              // eslint-disable-next-line no-console
              console.warn('Could not save password:', detail);
            }
          }
        } catch {
          /* password is best-effort; memory is already safely saved */
        }
      }

      // Fire-and-forget: trigger the soft "memory received" confirmation email.
      // Failures here must never block the success state for the customer.
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        if (backendUrl) {
          fetch(`${backendUrl}/api/memory/${encodeURIComponent(qr_code)}/notify-uploaded`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          }).catch(() => {});
        }
      } catch {
        /* ignore — email is best-effort */
      }

      setSaved(true);
    } catch (submitErr) {
      const msg = (submitErr && submitErr.message) ? submitErr.message : '';
      // Surface a photo-specific error inline when relevant, but always keep
      // the sender on this page so they can retry without losing their state.
      if (memoryType === 'photo') {
        setImageError(msg ? `Couldn\u2019t save your photo: ${msg}` : 'Couldn\u2019t save your photo. Please try again.');
      } else {
        setError('Could not save your memory. Please try again.');
        setStatus('error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center" data-testid="memory-loading">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center">
          <Loader2 className="mx-auto mb-4 text-[#C6A96B] animate-spin" size={32} strokeWidth={1.5} />
          <p className="text-sm tracking-[0.15em] uppercase text-[#6B6259]">Finding your memory space</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6" data-testid="memory-error">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-md text-center">
          <div className="w-12 h-12 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-6">
            <Heart className="text-[#C6A96B]" size={20} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#0B1F3A] mb-4 font-light" data-testid="error-heading">Memory Not Found</h1>
          <p className="text-[#6B6259] leading-relaxed text-sm sm:text-base" data-testid="error-message">{error}</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'locked') {
    // The sender has saved the memory — show the recipient's view.
    return <MemoryViewer qrCode={qr_code} />;
  }

  if (saved) {
    const vaultEnabled = !!(memory && memory.vault_enabled);
    return (
      <div className="min-h-screen bg-[#F7F5F2] flex items-center justify-center px-6" data-testid="memory-success">
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-[#C6A96B]/10 flex items-center justify-center mx-auto mb-8">
            <Heart className="text-[#C6A96B]" size={24} strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#0B1F3A] mb-5 font-light" data-testid="success-heading">Your Memory Is Saved</h1>
          <p className="text-[#6B6259] leading-[1.8] text-sm sm:text-base mb-4" data-testid="success-message">
            That's it — your words, your voice, your presence are now preserved inside this letter.
          </p>
          <p className="text-[#6B6259]/70 leading-[1.8] text-xs sm:text-sm">
            When the QR code is scanned, everything you've shared will be there — just as you left it. Thank you for creating something that truly lasts.
          </p>

          {/* Memory Vault post-upload CTA is hidden for the Letters That Last™-only
              launch. The block renders only when both the server flagged the order
              as vault-enabled AND the launch flag below is on. Flip this to re-enable. */}
          {false && vaultEnabled && (
            <div className="mt-12 pt-10 border-t border-[#C6A96B]/20" data-testid="memory-vault-cta-block">
              <p className="text-[10px] tracking-[0.35em] uppercase text-[#C6A96B] mb-4 font-semibold">Memory Vault · Included</p>
              <p className="text-[#6B6259] leading-[1.8] text-sm sm:text-base mb-7 px-2 italic" style={{ fontFamily: 'Georgia, serif' }}>
                A private space they can return to — with your voice, hidden messages and quiet reminders for the days that feel heavy.
              </p>
              <a
                href={`/vault-edit/${encodeURIComponent(qr_code)}`}
                data-testid="memory-vault-continue-cta"
                className="inline-block bg-[#C6A96B] text-white font-semibold tracking-[0.2em] uppercase text-xs px-10 py-4 transition-all duration-300 hover:bg-[#b89a5e] hover:shadow-[0_8px_28px_rgba(198,169,107,0.35)]"
              >
                Continue to Memory Vault
              </a>
              <p className="text-[#6B6259]/60 text-[11px] italic mt-4" style={{ fontFamily: 'Georgia, serif' }}>
                You can return to this anytime — there's no rush.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2]" data-testid="memory-upload-page">
      <div className="max-w-lg mx-auto px-6 py-20 sm:py-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>

          <div className="text-center mb-14">
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#C6A96B] mb-4 font-medium" data-testid="upload-overline">
              Sent2ment
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#0B1F3A] font-light mb-3" data-testid="upload-heading">
              Add Your Memory
            </h1>
            {memory?.title && (
              <p className="text-[#6B6259] text-sm" data-testid="upload-title">{memory.title}</p>
            )}
            <p
              className="text-xs italic text-[#6B6259] leading-relaxed max-w-md mx-auto mt-4"
              style={{ fontFamily: 'Georgia, serif' }}
              data-testid="upload-page-reassurance"
            >
              No rush. We&rsquo;ve also emailed you this link, so you can return whenever it feels right.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">

            {/* Memory Type Selector */}
            <div data-testid="memory-type-selector">
              <label className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-4">
                How would you like to leave your memory?
              </label>
              <div className="space-y-3">
                {MEMORY_TYPES.map(({ id, label, description, Icon }) => {
                  const selected = memoryType === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => handleTypeSelect(id)}
                      data-testid={`memory-type-${id}-btn`}
                      aria-pressed={selected}
                      className={`w-full flex items-center gap-4 text-left px-5 py-4 bg-white border transition-all duration-300 ${
                        selected
                          ? 'border-[#C6A96B] shadow-[0_1px_0_0_#C6A96B]'
                          : 'border-[#C6A96B]/20 hover:border-[#C6A96B]/50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition-colors duration-300 ${selected ? 'bg-[#C6A96B]/15' : 'bg-[#C6A96B]/8'}`}>
                        <Icon className="text-[#C6A96B]" size={18} strokeWidth={1.5} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[#0B1F3A] font-medium leading-tight">{label}</p>
                        <p className="text-xs text-[#6B6259] mt-1 leading-relaxed">{description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Recipient name */}
            <div>
              <label htmlFor="recipient-name" className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                Recipient's Name
              </label>
              <input
                id="recipient-name"
                type="text"
                value={recipientName}
                onChange={e => setRecipientName(e.target.value)}
                placeholder="Who is this for?"
                data-testid="recipient-name-input"
                className="w-full bg-white border border-[#C6A96B]/20 focus:border-[#C6A96B]/50 outline-none px-4 py-3 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300"
              />
            </div>

            {/* Conditional upload fields */}
            <AnimatePresence mode="wait">
              {memoryType === 'voice' && (
                <motion.div
                  key="voice"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                    Voice Message
                  </label>
                  <p
                    className="italic mb-3"
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: '#9A8B72',
                      fontSize: '13px',
                      letterSpacing: '0.02em',
                    }}
                    data-testid="audio-time-limit-hint"
                  >
                    Voice note &middot; up to 3 minutes &mdash; gentle and unhurried.
                  </p>
                  {audioFile ? (
                    <div className="flex items-center justify-between bg-white border border-[#C6A96B]/20 px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Mic className="text-[#C6A96B] shrink-0" size={16} strokeWidth={1.5} />
                        <span className="text-sm text-[#0B1F3A] truncate">{audioFile.name || 'Voice message selected'}</span>
                      </div>
                      <button type="button" onClick={() => setAudioFile(null)} className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid="remove-audio-btn">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => openPicker(audioInputRef)}
                        className="w-full text-left"
                        data-testid="audio-upload-zone-btn"
                      >
                        <div className="border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 px-4 py-6 text-center transition-colors duration-300" data-testid="audio-upload-zone">
                          <Mic className="text-[#C6A96B] mx-auto mb-2" size={20} strokeWidth={1.5} />
                          <p className="text-xs text-[#6B6259]">Upload a voice message</p>
                        </div>
                      </button>
                      <input
                        ref={audioInputRef}
                        id="audio-input"
                        type="file"
                        accept="audio/*"
                        className="sr-only"
                        data-testid="audio-file-input"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          e.target.value = '';
                          if (!f) return;
                          // Soft 3-minute limit to match the displayed hint.
                          // Falls through (accepts the file) if duration can't
                          // be probed so we never block a valid recording.
                          try {
                            const url = URL.createObjectURL(f);
                            const a = new Audio();
                            a.preload = 'metadata';
                            a.onloadedmetadata = () => {
                              URL.revokeObjectURL(url);
                              if (Number.isFinite(a.duration) && a.duration > 180.5) {
                                alert('This voice note is a little long — please keep it under 3 minutes.');
                              } else {
                                setAudioFile(f);
                              }
                            };
                            a.onerror = () => { URL.revokeObjectURL(url); setAudioFile(f); };
                            a.src = url;
                          } catch {
                            setAudioFile(f);
                          }
                        }}
                      />
                    </>
                  )}
                </motion.div>
              )}

              {memoryType === 'video' && (
                <motion.div
                  key="video"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                    Video Message <span className="normal-case tracking-normal text-[#6B6259]/70">(30 seconds max)</span>
                  </label>
                  {videoFile ? (
                    <div className="flex items-center justify-between bg-white border border-[#C6A96B]/20 px-4 py-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Video className="text-[#C6A96B] shrink-0" size={16} strokeWidth={1.5} />
                        <span className="text-sm text-[#0B1F3A] truncate">{videoFile.name || 'Video selected'}</span>
                      </div>
                      <button type="button" onClick={() => { setVideoFile(null); setVideoError(''); }} className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors" data-testid="remove-video-btn">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => openPicker(videoInputRef)}
                        className="w-full text-left"
                        data-testid="video-upload-zone-btn"
                      >
                        <div className="border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 px-4 py-6 text-center transition-colors duration-300" data-testid="video-upload-zone">
                          <Video className="text-[#C6A96B] mx-auto mb-2" size={20} strokeWidth={1.5} />
                          <p className="text-xs text-[#6B6259]">Upload a short video</p>
                        </div>
                      </button>
                      <input
                        ref={videoInputRef}
                        id="video-input"
                        type="file"
                        accept="video/*"
                        className="sr-only"
                        data-testid="video-file-input"
                        onChange={handleVideoAdd}
                      />
                    </>
                  )}
                  {videoError && (
                    <p className="text-xs text-red-400 mt-2" data-testid="video-error">{videoError}</p>
                  )}
                </motion.div>
              )}

              {memoryType === 'photo' && (
                <motion.div
                  key="photo"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4 }}
                >
                  <label className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                    Photos <span className="normal-case tracking-normal text-[#6B6259]/70">(up to 3)</span>
                  </label>
                  <div className="space-y-2" data-testid="image-list">
                    <AnimatePresence>
                      {imageItems.map((it) => (
                        <motion.div
                          key={it.id}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="flex items-center gap-3 bg-white border border-[#C6A96B]/20 px-3 py-3"
                          data-testid={`image-item-${it.id}`}
                        >
                          <div className="relative w-14 h-14 shrink-0 overflow-hidden bg-[#F7F5F2] border border-[#C6A96B]/15">
                            {it.previewUrl ? (
                              <img
                                src={it.previewUrl}
                                alt="Selected photo preview"
                                className="w-full h-full object-cover"
                                data-testid={`image-preview-${it.id}`}
                              />
                            ) : (
                              <ImageIcon className="text-[#C6A96B] m-auto mt-5" size={16} strokeWidth={1.5} />
                            )}
                            {it.status === 'uploading' && (
                              <div className="absolute inset-0 bg-white/75 flex items-center justify-center">
                                <Loader2 className="text-[#C6A96B] animate-spin" size={18} strokeWidth={1.5} />
                              </div>
                            )}
                            {it.status === 'done' && (
                              <div className="absolute inset-0 bg-[#C6A96B]/85 flex items-center justify-center">
                                <Check className="text-white" size={20} strokeWidth={2} />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-[#0B1F3A] truncate">
                              {it.file?.name || 'Photo selected'}
                            </p>
                            <p className="text-[11px] mt-0.5 leading-snug">
                              {it.status === 'ready' && (
                                <span className="text-[#C6A96B] inline-flex items-center gap-1">
                                  <Check size={11} strokeWidth={2} /> Photo selected
                                </span>
                              )}
                              {it.status === 'uploading' && (
                                <span className="text-[#6B6259] inline-flex items-center gap-1" data-testid={`image-uploading-${it.id}`}>
                                  <Loader2 className="animate-spin" size={11} strokeWidth={2} /> Uploading…
                                </span>
                              )}
                              {it.status === 'done' && (
                                <span className="text-[#C6A96B] inline-flex items-center gap-1" data-testid={`image-done-${it.id}`}>
                                  <Check size={11} strokeWidth={2} /> Uploaded
                                </span>
                              )}
                              {it.status === 'error' && (
                                <span className="text-red-500 inline-flex items-center gap-1" data-testid={`image-error-${it.id}`}>
                                  <AlertCircle size={11} strokeWidth={2} /> {it.errorMessage || 'Upload failed'}
                                </span>
                              )}
                            </p>
                          </div>
                          {it.status !== 'uploading' && it.status !== 'done' && (
                            <button
                              type="button"
                              onClick={() => removeImage(it.id)}
                              className="text-[#6B6259] hover:text-[#0B1F3A] transition-colors shrink-0"
                              data-testid={`remove-image-${it.id}-btn`}
                              aria-label="Remove photo"
                            >
                              <X size={16} />
                            </button>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {imageItems.length > 0 && !submitting && (
                      <p className="text-[11px] text-[#C6A96B] pt-1 pl-1" data-testid="image-selected-confirmation">
                        <Check size={11} strokeWidth={2} className="inline -mt-0.5 mr-1" />
                        {imageItems.length} {imageItems.length === 1 ? 'photo' : 'photos'} ready — tap “Save & Seal This Memory” below.
                      </p>
                    )}

                    {imageItems.length < 3 && (
                      <>
                        {isInApp && (
                          <div
                            className="flex items-start gap-2 bg-[#C6A96B]/8 border border-[#C6A96B]/25 px-3 py-2.5 text-[11px] text-[#6B6259] leading-relaxed"
                            data-testid="image-inapp-warning"
                          >
                            <Info className="text-[#C6A96B] shrink-0 mt-0.5" size={13} strokeWidth={1.5} />
                            <span>
                              Some in-app browsers block photo uploads. If the picker doesn’t work, tap the <strong>•••</strong> menu and choose <strong>“Open in Chrome / Safari”</strong>.
                            </span>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2" data-testid="image-upload-zones">
                          {/* Gallery — no capture attribute, no multiple. Input is a
                              transparent full-size overlay so the tap always lands
                              directly on the native <input type=file>, bypassing
                              every Android quirk we've seen with sr-only inputs. */}
                          <label
                            className="relative block border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 active:border-[#C6A96B]/60 px-4 py-6 text-center transition-colors duration-300 cursor-pointer"
                            data-testid="image-upload-zone-btn"
                          >
                            <input
                              id="image-input"
                              type="file"
                              accept="image/*"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              style={{ fontSize: 0, WebkitAppearance: 'none' }}
                              data-testid="image-file-input"
                              onChange={handleImageAdd}
                            />
                            <div className="pointer-events-none" data-testid="image-upload-zone">
                              <ImageIcon className="text-[#C6A96B] mx-auto mb-2" size={20} strokeWidth={1.5} />
                              <p className="text-xs text-[#0B1F3A] font-medium">Choose photo</p>
                              <p className="text-[10px] text-[#6B6259]/80 mt-1">From your gallery</p>
                            </div>
                          </label>

                          {/* Camera — capture="environment" tells mobile browsers to
                              launch the camera directly, skipping the chooser dialog
                              entirely. On desktop, capture is ignored and this opens
                              the normal file picker. */}
                          <label
                            className="relative block border border-dashed border-[#C6A96B]/20 hover:border-[#C6A96B]/40 active:border-[#C6A96B]/60 px-4 py-6 text-center transition-colors duration-300 cursor-pointer"
                            data-testid="image-camera-btn"
                          >
                            <input
                              id="image-camera-input"
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                              style={{ fontSize: 0, WebkitAppearance: 'none' }}
                              data-testid="image-camera-input"
                              onChange={handleImageAdd}
                            />
                            <div className="pointer-events-none">
                              <Camera className="text-[#C6A96B] mx-auto mb-2" size={20} strokeWidth={1.5} />
                              <p className="text-xs text-[#0B1F3A] font-medium">Take photo</p>
                              <p className="text-[10px] text-[#6B6259]/80 mt-1">Use your camera</p>
                            </div>
                          </label>
                        </div>

                        <p className="text-[11px] text-[#6B6259]/75 text-center pt-1" data-testid="image-upload-hint">
                          {imageItems.length === 0
                            ? 'Choose from your camera roll or snap a new photo'
                            : `${3 - imageItems.length} ${3 - imageItems.length === 1 ? 'photo' : 'photos'} left — add another from your gallery or camera`}
                        </p>
                      </>
                    )}

                    {imageError && (
                      <p className="text-xs text-red-500 pt-1" data-testid="image-error">
                        {imageError}
                      </p>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Optional password protection */}
            <div data-testid="memory-password-section" className="border-t border-[#C6A96B]/15 pt-8">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={protectEnabled}
                    onChange={(e) => {
                      setProtectEnabled(e.target.checked);
                      if (!e.target.checked) {
                        setPwd(''); setPwdConfirm(''); setPwdHint(''); setPwdError('');
                      }
                    }}
                    data-testid="protect-memory-toggle"
                    className="peer sr-only"
                  />
                  <div className="w-[18px] h-[18px] border border-[#C6A96B]/40 bg-white peer-checked:bg-[#C6A96B] peer-checked:border-[#C6A96B] transition-colors duration-300 flex items-center justify-center">
                    {protectEnabled && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#FFFFFF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="text-[#C6A96B]" size={15} strokeWidth={1.5} />
                    <p className="text-sm text-[#0B1F3A] font-medium leading-tight">Protect this memory <span className="text-[#6B6259] font-normal">(optional)</span></p>
                  </div>
                  <p className="text-xs text-[#6B6259] mt-1.5 leading-relaxed">
                    You can choose to protect this memory with a password if you’d like to keep it private.
                  </p>
                </div>
              </label>

              <AnimatePresence initial={false}>
                {protectEnabled && (
                  <motion.div
                    key="pwd-fields"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pt-5 pl-7 space-y-4">
                      <div>
                        <label htmlFor="memory-pwd" className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            id="memory-pwd"
                            type={showPwd ? 'text' : 'password'}
                            value={pwd}
                            onChange={(e) => setPwd(e.target.value)}
                            autoComplete="new-password"
                            data-testid="memory-password-input"
                            placeholder="Choose a password"
                            className="w-full bg-white border border-[#C6A96B]/20 focus:border-[#C6A96B]/50 outline-none px-4 py-3 pr-10 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPwd((v) => !v)}
                            data-testid="memory-password-toggle-visibility"
                            aria-label={showPwd ? 'Hide password' : 'Show password'}
                            className="absolute inset-y-0 right-0 px-3 text-[#6B6259] hover:text-[#0B1F3A] transition-colors"
                          >
                            {showPwd ? <EyeOff size={16} strokeWidth={1.5} /> : <Eye size={16} strokeWidth={1.5} />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="memory-pwd-confirm" className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                          Confirm password
                        </label>
                        <input
                          id="memory-pwd-confirm"
                          type={showPwd ? 'text' : 'password'}
                          value={pwdConfirm}
                          onChange={(e) => setPwdConfirm(e.target.value)}
                          autoComplete="new-password"
                          data-testid="memory-password-confirm-input"
                          placeholder="Enter it once more"
                          className="w-full bg-white border border-[#C6A96B]/20 focus:border-[#C6A96B]/50 outline-none px-4 py-3 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300"
                        />
                      </div>

                      <div>
                        <label htmlFor="memory-pwd-hint" className="block text-xs tracking-[0.2em] uppercase text-[#6B6259] mb-2">
                          Password hint <span className="normal-case tracking-normal text-[#6B6259]/70">(optional)</span>
                        </label>
                        <input
                          id="memory-pwd-hint"
                          type="text"
                          value={pwdHint}
                          onChange={(e) => setPwdHint(e.target.value)}
                          maxLength={120}
                          data-testid="memory-password-hint-input"
                          placeholder="Something gentle to help them remember"
                          className="w-full bg-white border border-[#C6A96B]/20 focus:border-[#C6A96B]/50 outline-none px-4 py-3 text-[#0B1F3A] text-sm placeholder:text-[#6B6259]/65 transition-colors duration-300"
                        />
                      </div>

                      {pwdError && (
                        <p className="text-xs text-red-400" data-testid="memory-password-error">{pwdError}</p>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="pt-4">
              <motion.button
                type="submit"
                disabled={!canSubmit}
                whileHover={!canSubmit ? {} : { scale: 1.01 }}
                whileTap={!canSubmit ? {} : { scale: 0.99 }}
                data-testid="save-memory-btn"
                className="w-full bg-[#C6A96B] text-white font-medium tracking-[0.2em] uppercase text-xs py-4 transition-all duration-300 hover:bg-[#b89a5e] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="animate-spin" size={14} /> Saving your memory...
                  </span>
                ) : (
                  'Save & Seal This Memory'
                )}
              </motion.button>
              {!memoryType && (
                <p className="text-xs text-[#6B6259]/70 text-center mt-3" data-testid="select-type-hint">
                  Please choose a memory type above to continue.
                </p>
              )}
              <p
                className="text-[11px] italic text-center mt-4 leading-relaxed"
                style={{ color: '#8A7F73', fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="upload-permission-disclosure"
              >
                By uploading, you confirm you have permission to share this content.
              </p>
              <p
                className="text-[11px] italic text-center mt-2 leading-relaxed"
                style={{ color: '#8A7F73', fontFamily: "'Cormorant Garamond', serif" }}
                data-testid="upload-storage-disclosure"
              >
                Securely preserved &mdash; made available through your keepsake.
              </p>
            </div>
          </form>

        </motion.div>
      </div>
    </div>
  );
};

export default MemoryUploadPage;
